import { motion } from "framer-motion";
import { Phone, MapPin, Star, Trophy, Users, Sparkles, HandHeart } from "lucide-react";
import { BUSINESS } from "../data/site";
import { Link } from "react-router-dom";

const TIMELINE = [
    { year: "2015", title: "Opened our doors", desc: "Started as a small storefront in the heart of Glendale." },
    { year: "2018", title: "Trusted activations partner", desc: "Authorized to activate on all major US carriers." },
    { year: "2021", title: "Expanded accessory catalog", desc: "Brought in premium brands like OtterBox, Anker and Spigen." },
    { year: "2024", title: "4.8★ from our community", desc: "Hundreds of activations, upgrades and happy customers later." },
];

export default function About() {
    return (
        <>
            <section className="relative overflow-hidden">
                <div className="hero-glow" />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Our Story</div>
                        <h1 className="mt-3 font-display font-extrabold text-slate-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            Built on trust, expertise, and Glendale loyalty.
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                            EG Wireless was founded with one simple idea: wireless service shouldn't feel like a high-pressure sales pitch. We help our neighbors choose the right phone, the right plan, and the right accessories — at fair prices, with no upsell tactics.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition">
                                <Phone className="w-4 h-4" /> Call the store
                            </a>
                            <Link to="/contact" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border border-slate-200 text-slate-900 font-semibold hover:bg-slate-50 transition">
                                Get in touch
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-3xl overflow-hidden premium-shadow">
                            <img src="https://images.pexels.com/photos/11297769/pexels-photo-11297769.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Store" className="w-full h-[420px] object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="bg-slate-50">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 grid lg:grid-cols-4 sm:grid-cols-2 gap-5">
                    {[
                        [HandHeart, "Customer-first", "We treat you like a neighbor, not a transaction."],
                        [Sparkles, "Honest pricing", "Fair prices on devices, accessories and trade-ins."],
                        [Trophy, "Real expertise", "Years of hands-on smartphone and carrier knowledge."],
                        [Users, "Long-term relationships", "Generations of Glendale customers keep returning."],
                    ].map(([Icon, t, d], i) => (
                        <motion.div
                            key={t}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.06 }}
                            className="bg-white rounded-2xl border border-slate-100 premium-shadow p-6"
                        >
                            <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-600 grid place-items-center mb-4">
                                <Icon className="w-5 h-5" />
                            </div>
                            <div className="font-display font-bold text-slate-900">{t}</div>
                            <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Timeline */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
                <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Our journey</div>
                <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight max-w-2xl">
                    A trusted Glendale name, year after year.
                </h2>
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {TIMELINE.map((t, i) => (
                        <motion.div
                            key={t.year}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.07 }}
                            className="relative pl-6 border-l-2 border-blue-100"
                        >
                            <div className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-blue-600" />
                            <div className="text-xs font-semibold text-blue-600">{t.year}</div>
                            <div className="font-display font-bold text-slate-900 mt-2">{t.title}</div>
                            <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{t.desc}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
                <div className="rounded-3xl bg-slate-900 text-white p-10 lg:p-14 grid lg:grid-cols-2 gap-8 items-center premium-shadow overflow-hidden relative">
                    <div className="absolute inset-0 opacity-30" style={{
                        background: "radial-gradient(30rem 20rem at 80% 30%, rgba(37,99,235,0.4), transparent 60%)",
                    }} />
                    <div className="relative">
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 inline-block" />
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 inline-block" />
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 inline-block" />
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 inline-block" />
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 inline-block" />
                        <h3 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight max-w-lg">
                            Come see why Glendale trusts EG Wireless.
                        </h3>
                    </div>
                    <div className="relative flex flex-col sm:flex-row gap-3 lg:justify-end">
                        <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition">
                            <Phone className="w-4 h-4" /> {BUSINESS.phone}
                        </a>
                        <a href={BUSINESS.mapsDirections} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition">
                            <MapPin className="w-4 h-4" /> Visit Store
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
}
